import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card, Typography, Table, Tag, Tabs, Row, Col, Input, Select,
  Button, Space, message, Popconfirm, Tooltip,
} from "antd";
import {
  SearchOutlined, ClearOutlined, CheckCircleOutlined, UndoOutlined,
  ExclamationCircleOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  listarInconvenientesRit,
  obtenerConteoInconvenientesRit,
  cambiarEstadoInconvenienteRit,
} from "../../services/reporteRit/ritInconvenientesService";

const { Title, Text } = Typography;
const { Option } = Select;

const MODULO_LABEL = { 1: "Entregas", 2: "Entregas 2", 3: "Entregas 3" };
const PAGE_SIZE = 50;

const RitInconvenientes = () => {
  const permissions      = useSelector((state) => selectAppPermissions(state, "rit_inconvenientes"));
  const permissionUpdate = permissions.priv_update === "Y";

  const [estadoTab, setEstadoTab]     = useState("PENDIENTE");
  const [data, setData]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);
  const [conteo, setConteo]           = useState({ PENDIENTE: 0, CORREGIDO: 0 });

  const [filtroModulo, setFiltroModulo]         = useState(null);
  const [filtroMunicipio, setFiltroMunicipio]   = useState("");
  const [filtroDocumento, setFiltroDocumento]   = useState("");

  const cargar = useCallback(async (paginaActual = page) => {
    setLoading(true);
    try {
      const resultado = await listarInconvenientesRit({
        estado: estadoTab,
        modulo: filtroModulo || undefined,
        municipio: filtroMunicipio || undefined,
        documento: filtroDocumento || undefined,
        page: paginaActual,
        pageSize: PAGE_SIZE,
      });
      if (resultado?.success) {
        setData(resultado.data || []);
        setTotal(resultado.total || 0);
      }
    } catch {
      message.error("Error al consultar los participantes con inconvenientes");
    } finally {
      setLoading(false);
    }
  }, [estadoTab, filtroModulo, filtroMunicipio, filtroDocumento, page]);

  const cargarConteo = useCallback(async () => {
    try {
      const resultado = await obtenerConteoInconvenientesRit(filtroModulo || undefined);
      if (resultado?.success) setConteo(resultado.data);
    } catch {
      // El conteo de las pestañas es informativo; si falla no bloquea el listado
    }
  }, [filtroModulo]);

  useEffect(() => {
    setPage(1);
    cargar(1);
    cargarConteo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoTab, filtroModulo, filtroMunicipio, filtroDocumento]);

  const handleBuscar = () => { setPage(1); cargar(1); };

  const handleLimpiar = () => {
    setFiltroModulo(null);
    setFiltroMunicipio("");
    setFiltroDocumento("");
  };

  const handleCambiarEstado = async (id, nuevoEstado) => {
    try {
      const resultado = await cambiarEstadoInconvenienteRit(id, nuevoEstado);
      if (resultado?.success) {
        message.success(
          nuevoEstado === "CORREGIDO"
            ? "Participante marcado como corregido"
            : "Participante devuelto a pendiente"
        );
        cargar(page);
        cargarConteo();
      }
    } catch {
      message.error("Error al cambiar el estado del participante");
    }
  };

  const columns = [
    {
      title: "Módulo", dataIndex: "modulo", key: "modulo", width: 110, align: "center",
      render: (val) => <Tag color="geekblue">{MODULO_LABEL[val] || `Módulo ${val}`}</Tag>,
    },
    { title: "Documento", dataIndex: "numero_documento_titular", key: "documento", width: 140,
      render: (val) => val || "—" },
    { title: "Nombre", dataIndex: "nombre_titular", key: "nombre_titular", ellipsis: true, width: 200,
      render: (val) => val || "—" },
    { title: "N° Entrega", dataIndex: "nro_entrega", key: "nro_entrega", width: 110, align: "center",
      render: (val) => val || "—" },
    { title: "Municipio", dataIndex: "municipio", key: "municipio", width: 150,
      render: (val) => val || <Text type="secondary">Vacío</Text> },
    { title: "Departamento", dataIndex: "departamento", key: "departamento", width: 150,
      render: (val) => val || <Text type="secondary">Vacío</Text> },
    {
      title: "Observación", dataIndex: "observacion", key: "observacion", ellipsis: true, width: 320,
      render: (val) => (
        <Tooltip title={val}>
          <Text type="danger"><ExclamationCircleOutlined style={{ marginRight: 4 }} />{val}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Última detección", dataIndex: "fecha_ultima_deteccion", key: "fecha_ultima_deteccion", width: 170,
      render: (val) => val ? new Date(val).toLocaleString("es-CO") : "—",
    },
    {
      title: "Acción", key: "accion", width: 160, fixed: "right",
      render: (_, row) => (
        !permissionUpdate ? null :
        row.estado === "PENDIENTE" ? (
          <Popconfirm
            title="¿Marcar este participante como corregido?"
            onConfirm={() => handleCambiarEstado(row.id, "CORREGIDO")}
            okText="Sí" cancelText="No"
          >
            <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
              Marcar corregido
            </Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title="¿Volver a marcar como pendiente?"
            onConfirm={() => handleCambiarEstado(row.id, "PENDIENTE")}
            okText="Sí" cancelText="No"
          >
            <Button size="small" icon={<UndoOutlined />}>
              Revertir
            </Button>
          </Popconfirm>
        )
      ),
    },
  ];

  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Participantes con inconvenientes RIT</Title>
      <Tabs
        style={{ marginTop: 16 }}
        activeKey={estadoTab}
        onChange={setEstadoTab}
        items={[
          { key: "PENDIENTE", label: `Con error (${conteo.PENDIENTE ?? 0})` },
          { key: "CORREGIDO", label: `Corregidos (${conteo.CORREGIDO ?? 0})` },
        ]}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={8} md={5}>
          <Select
            placeholder="Módulo"
            style={{ width: "100%" }}
            value={filtroModulo}
            onChange={setFiltroModulo}
            allowClear
          >
            <Option value={1}>Entregas</Option>
            <Option value={2}>Entregas 2</Option>
            <Option value={3}>Entregas 3</Option>
          </Select>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Input
            placeholder="Buscar por municipio"
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            value={filtroMunicipio}
            onChange={(e) => setFiltroMunicipio(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Input
            placeholder="Buscar por documento"
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            value={filtroDocumento}
            onChange={(e) => setFiltroDocumento(e.target.value.replace(/\D/g, ""))}
            allowClear
          />
        </Col>
        <Col>
          <Space>
            <Button icon={<SearchOutlined />} onClick={handleBuscar}>Buscar</Button>
            <Button icon={<ClearOutlined />} onClick={handleLimpiar}>Limpiar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { cargar(page); cargarConteo(); }}>Actualizar</Button>
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        bordered
        size="middle"
        scroll={{ x: 1400 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (t, range) => `${range[0]}–${range[1]} de ${t} registros`,
          onChange: (nuevaPagina) => { setPage(nuevaPagina); cargar(nuevaPagina); },
        }}
      />
    </Card>
  );
};

export default RitInconvenientes;
